import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  ArrowLeft, 
  Check, 
  Globe, 
  Briefcase, 
  Code,
  FileText,
  Calendar,
  Calculator,
  Users,
  Send,
  Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const projectTypes = [
  {
    id: 'site_simple',
    label: 'Site web simple',
    price: 'dès 400 CHF',
    icon: Globe,
    description: 'Site vitrine 1-5 pages'
  },
  {
    id: 'site_pro',
    label: 'Site web professionnel',
    price: 'dès 800 CHF',
    icon: Briefcase,
    description: 'Site complet avec fonctionnalités avancées'
  },
  {
    id: 'app_web',
    label: 'Application web sur mesure',
    price: 'Devis personnalisé',
    icon: Code,
    description: 'Solution personnalisée pour vos besoins'
  }
];

const tools = [
  {
    icon: FileText,
    title: 'Générateur de devis',
    audience: 'Artisans, menuisiers, plombiers, entreprises de construction',
    description: 'Le client sélectionne matériaux, dimensions, options et le prix se calcule automatiquement.'
  },
  {
    icon: Calendar,
    title: 'Système de réservation',
    audience: 'Thérapeutes, coachs, salons, cabinets',
    description: 'Les clients prennent rendez-vous directement en ligne.'
  },
  {
    icon: Calculator,
    title: 'Calculateur de prix',
    audience: 'Menuiserie, construction, services',
    description: 'Calcul automatique pour terrasse bois, installation, prestations.'
  }
];

const includedFeatures = [
  'Design professionnel',
  'Site mobile responsive',
  'Formulaire de contact',
  'Optimisation Google',
  'Mise en ligne'
];

export const PricingPage = () => {
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    project_type: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (location.hash === '#quote-form') {
      setTimeout(() => {
        const element = document.getElementById('quote-form');
        if (element) {
          const offset = element.offsetTop - 100;
          window.scrollTo({ top: offset, behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.project_type) {
      toast.error('Veuillez sélectionner un type de projet');
      return;
    }
    if (!formData.name || !formData.email || !formData.company || !formData.phone || !formData.message) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API_URL}/quote`, formData);
      toast.success('Votre demande de devis a été envoyée ! Nous vous contacterons sous 24h.');
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        project_type: '',
        message: ''
      });
    } catch (error) {
      toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="pricing-page">
      <Header />
      
      <main className="pt-24 md:pt-32">
        {/* Back button */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-[#4A4A4A] hover:text-[#1a365d] transition-colors"
            data-testid="back-link"
          >
            <ArrowLeft size={18} />
            Retour à l'accueil
          </Link>
        </div>

        {/* Hero */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-20">
          <h1 
            data-testid="pricing-title"
            className="font-['Manrope'] text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1A1A1A] mb-6"
          >
            Sites web et outils digitaux<br />
            <span className="gradient-text">pour les entreprises</span>
          </h1>
          <p className="text-base lg:text-lg text-[#4A4A4A] max-w-2xl mx-auto">
            Sites modernes, rapides et outils professionnels pour automatiser votre activité.
          </p>
        </section>

        {/* Main Offer */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24">
          <div className="bg-gradient-to-br from-[#1a365d] to-[#0f2440] rounded-3xl p-8 md:p-12 text-white relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
            
            <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block text-xs font-medium text-white/80 uppercase tracking-wider mb-4">
                  Offre principale
                </span>
                <h2 
                  data-testid="main-offer-title"
                  className="font-['Manrope'] text-3xl sm:text-4xl font-bold mb-6"
                >
                  Site web et Application digitale<br />
                  <span className="text-white/90">sur mesure</span>
                </h2>
                <p className="text-white/80 mb-8">
                  Des solutions digitales personnalisées pour votre entreprise.
                </p>
                <Button 
                  data-testid="main-offer-cta"
                  onClick={() => {
                    const form = document.getElementById('quote-form');
                    if (form) {
                      const offset = form.offsetTop - 100;
                      window.scrollTo({ top: offset, behavior: 'smooth' });
                    }
                  }}
                  className="bg-white text-[#1a365d] hover:bg-[#F0F0F0] rounded-full px-8 py-6 text-sm font-semibold transition-all hover:scale-105"
                >
                  Demander un devis
                </Button>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
                <h3 className="font-['Manrope'] font-semibold text-lg mb-6">Inclus dans l'offre :</h3>
                <ul className="space-y-4">
                  {includedFeatures.map((feature) => (
                    <li key={feature} className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                        <Check size={14} className="text-white" />
                      </div>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Web Tools Section */}
        <section className="bg-[#F5F5F7] py-24 mb-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="inline-block text-xs font-medium text-[#1a365d] uppercase tracking-wider mb-4">
                Solutions sur mesure
              </span>
              <h2 
                data-testid="tools-title"
                className="font-['Manrope'] text-3xl sm:text-4xl font-bold text-[#1A1A1A] mb-4"
              >
                Outils web professionnels<br />pour votre entreprise
              </h2>
              <p className="text-base text-[#4A4A4A] max-w-2xl mx-auto">
                Nous développons également des applications web sur mesure pour simplifier 
                votre travail et automatiser certaines tâches.
              </p>
            </div>

            {/* Quick features list */}
            <div className="flex flex-wrap justify-center gap-4 mb-16">
              {['Générateur de devis automatique', 'Système de réservation', 'Gestion de clients', 
                'Formulaires intelligents', 'Calculateurs de prix', 'Espace client'].map((feature) => (
                <div 
                  key={feature}
                  className="flex items-center gap-2 bg-white rounded-full px-4 py-2 text-sm"
                >
                  <Check size={14} className="text-[#10B981]" />
                  <span className="text-[#1A1A1A]">{feature}</span>
                </div>
              ))}
            </div>

            {/* Tool Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {tools.map((tool, index) => (
                <div
                  key={tool.title}
                  data-testid={`tool-card-${index}`}
                  className="bg-white p-8 rounded-2xl border border-[#D0D0D0] hover:border-[#1a365d] transition-all hover:-translate-y-1"
                >
                  <div className="tool-icon-bg w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                    <tool.icon className="text-[#1a365d]" size={28} />
                  </div>
                  <h3 className="font-['Manrope'] text-xl font-bold text-[#1A1A1A] mb-2">
                    {tool.title}
                  </h3>
                  <p className="text-xs text-[#1a365d] font-medium mb-3">
                    Pour : {tool.audience}
                  </p>
                  <p className="text-sm text-[#4A4A4A] leading-relaxed">
                    {tool.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Quote Form Section */}
        <section id="quote-form" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 pt-8">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block text-xs font-medium text-[#1a365d] uppercase tracking-wider mb-4">
                Configurateur
              </span>
              <h2 
                data-testid="form-title"
                className="font-['Manrope'] text-3xl sm:text-4xl font-bold text-[#1A1A1A] mb-4"
              >
                Demander un devis
              </h2>
              <p className="text-base text-[#4A4A4A]">
                Sélectionnez le type de projet et remplissez le formulaire pour recevoir votre devis.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8" data-testid="quote-form">
              {/* Project Type Selection */}
              <div className="space-y-4">
                <Label className="text-base font-semibold text-[#1A1A1A]">
                  Type de projet <span className="text-red-500">*</span>
                </Label>
                <RadioGroup
                  value={formData.project_type}
                  onValueChange={(value) => setFormData({ ...formData, project_type: value })}
                  className="grid grid-cols-1 md:grid-cols-3 gap-4"
                  data-testid="project-type-selection"
                >
                  {projectTypes.map((type) => (
                    <div key={type.id}>
                      <RadioGroupItem
                        value={type.id}
                        id={type.id}
                        className="peer sr-only"
                      />
                      <Label
                        htmlFor={type.id}
                        data-testid={`project-type-${type.id}`}
                        className={`flex flex-col p-6 rounded-xl border-2 cursor-pointer transition-all ${
                          formData.project_type === type.id
                            ? 'border-[#003399] bg-[#003399]/5'
                            : 'border-[#D0D0D0] hover:border-[#003399]/50'
                        }`}
                      >
                        <type.icon 
                          size={24} 
                          className={formData.project_type === type.id ? 'text-[#003399]' : 'text-[#555555]'} 
                        />
                        <span className="font-['Manrope'] font-semibold text-[#1A1A1A] mt-3">
                          {type.label}
                        </span>
                        <span className="text-[#003399] font-bold text-lg mt-1">
                          {type.price}
                        </span>
                        <span className="text-xs text-[#555555] mt-2">
                          {type.description}
                        </span>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              {/* Contact Information */}
              <div className="bg-[#F0F0F0] rounded-2xl p-8 space-y-6">
                <h3 className="font-['Manrope'] font-semibold text-lg text-[#1A1A1A]">
                  Vos coordonnées
                </h3>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nom <span className="text-red-500">*</span></Label>
                    <Input
                      id="name"
                      data-testid="quote-name-input"
                      placeholder="Votre nom"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="bg-white border-[#D0D0D0] focus:border-[#1a365d] h-12 rounded-lg"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company">Entreprise <span className="text-red-500">*</span></Label>
                    <Input
                      id="company"
                      data-testid="quote-company-input"
                      placeholder="Votre entreprise"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="bg-white border-[#D0D0D0] focus:border-[#1a365d] h-12 rounded-lg"
                      required
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Téléphone <span className="text-red-500">*</span></Label>
                    <Input
                      id="phone"
                      data-testid="quote-phone-input"
                      type="tel"
                      placeholder="+41 XX XXX XX XX"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="bg-white border-[#D0D0D0] focus:border-[#1a365d] h-12 rounded-lg"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email <span className="text-red-500">*</span></Label>
                    <Input
                      id="email"
                      data-testid="quote-email-input"
                      type="email"
                      placeholder="votre@email.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="bg-white border-[#D0D0D0] focus:border-[#1a365d] h-12 rounded-lg"
                      required
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="message">Message <span className="text-red-500">*</span></Label>
                  <Textarea
                    id="message"
                    data-testid="quote-message-input"
                    placeholder="Décrivez votre projet ou posez vos questions..."
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="bg-white border-[#D0D0D0] focus:border-[#1a365d] min-h-[120px] rounded-lg resize-none"
                    required
                  />
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                data-testid="quote-submit-btn"
                disabled={isSubmitting}
                className="w-full bg-[#1a365d] hover:bg-[#0f2440] text-white rounded-full h-14 text-base font-semibold transition-all hover:scale-[1.02] shadow-lg shadow-slate-700/20"
              >
                {isSubmitting ? (
                  <Loader2 className="animate-spin mr-2" size={20} />
                ) : (
                  <Send className="mr-2" size={20} />
                )}
                Recevoir mon devis
              </Button>
              
              {/* Trust indicators */}
              <div className="flex flex-wrap justify-center gap-6 text-sm text-[#4A4A4A]">
                <div className="flex items-center gap-2">
                  <Check size={16} className="text-[#10B981]" />
                  Devis gratuit
                </div>
                <div className="flex items-center gap-2">
                  <Check size={16} className="text-[#10B981]" />
                  Sans engagement
                </div>
                <div className="flex items-center gap-2">
                  <Check size={16} className="text-[#10B981]" />
                  Réponse sous 24h
                </div>
              </div>
            </form>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PricingPage;
